import React, { useEffect, useMemo } from 'react';
import './App.css';
import './styles/responsive.css'; // Must load AFTER App.css for overrides to work
import { SPLASHES } from './config/animationTimings';

// Phase Management
import { PhaseManager } from './phases/PhaseManager';

// Toast Notifications (global - in allen Phasen verfügbar)
import { ToastContainer } from './components/notifications';

// Custom Hooks
import { useSocketHandlers } from './hooks';
import { useHostCardBack } from './hooks/useHostCardBack';
import { useGameSessionGuard } from './hooks/useGameSessionGuard';

// State Management
import { useGameDispatch, useGameState } from './state';

// Socket Service - connect early for dev tools
import socketService from './socketService';

function App() {
  // Connect socket on app start (needed for Balancing Editor etc.)
  useEffect(() => {
    socketService.connect();
  }, []);
  // GameContext - zentrales State Management
  const dispatch = useGameDispatch();
  const globalState = useGameState();

  // Player identification (from GameContext)
  const myPlayerIndex = globalState.myPlayerIndex ?? null;
  const players = useMemo(() => globalState.players || [], [globalState.players]);
  const isLocalTestMode = globalState.isLocalTestMode || false;

  // === GameContext Reads ===
  const gc = {
    showShop: globalState.showShop || false,
    pendingRoundEnd: globalState.pendingRoundEnd || null,
    finishedPlayerSplash: globalState.finishedPlayerSplash || null,
    endSequence: globalState.endSequence || null, // Neue choreographierte Endsequenz
  };

  // === Socket event handlers hook ===
  const { registerListeners, unregisterListeners } = useSocketHandlers({
    dispatch,
    players,
    myPlayerIndex,
    isLocalTestMode,
    showShop: gc.showShop,
  });

  // Socket.io event listeners
  useEffect(() => {
    registerListeners();
    return () => unregisterListeners();
  }, [registerListeners, unregisterListeners]);

  // Apply host's card back CSS to all players during game
  useHostCardBack();

  // Protect active game sessions from browser back/reload
  useGameSessionGuard(globalState.phase);

  // Effect to show pending round end after splash is dismissed
  // Wait 1500ms to give users time to see the last played card
  // IMPORTANT: Also wait for endSequence to complete (new choreographed sequence)
  useEffect(() => {
    // Block round end transition while end sequence is active
    if (gc.pendingRoundEnd && !gc.finishedPlayerSplash && !gc.endSequence) {
      const timer = setTimeout(() => {
        dispatch({
          type: 'GAME_STATE_UPDATE',
          payload: {
            rankings: gc.pendingRoundEnd.rankings,
            roundStats: gc.pendingRoundEnd.roundStats,
            roundIncome: gc.pendingRoundEnd.roundIncome,
            points: gc.pendingRoundEnd.points,
            coins: gc.pendingRoundEnd.coins,
            mult: gc.pendingRoundEnd.mult,
            showRoundEnd: true,
            pendingRoundEnd: null,
          },
        });
      }, SPLASHES.pendingRoundEnd);
      return () => clearTimeout(timer);
    }
  }, [gc.pendingRoundEnd, gc.finishedPlayerSplash, gc.endSequence, dispatch]);

  // PhaseManager renders everything based on currentPhase
  return (
    <>
      <PhaseManager />
      {/* Toast Notifications - global, in allen Phasen verfügbar */}
      <ToastContainer />
    </>
  );
}

export default App;
